import type { ImageProps as UdsImageProps } from '@sunwebgroup/uds-ui/image'
import { space, tok } from '@sunwebgroup/ui-components'
import {
  Icon,
  Typography,
  LazyImage as UdsLazyImage,
} from '@sunwebgroup/ui-components/uds'
import { BaseHTMLAttributes, ImgHTMLAttributes, JSX, useEffect, useState } from 'react'
import { useMountedState } from 'react-use'
import { styled } from 'styled-components'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'

interface ImageComponentProps extends Omit<UdsImageProps, 'srcSet'> {
  src: string
  srcSet?: Array<number>
}

const defaultSrcSet: number[] = []

const DEFAULT_PLACEHOLDER_IMAGE_WIDTH = 20

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export function Image({ srcSet = defaultSrcSet, src, ...props }: ImageComponentProps) {
  const isMounted = useMountedState()

  if (!isMounted)
    return null

  return (
    <UdsLazyImage
      src={srcSet.length > 0 ? buildImgSrc(src, srcSet[0]) : src}
      srcSet={srcSet.length > 0 ? buildImgSrcSet(src, srcSet) : undefined}
      placeholderSrc={buildImgSrc(src, DEFAULT_PLACEHOLDER_IMAGE_WIDTH)}
      {...props}
    />
  )
}

Image.displayName = 'Image'

export default Image

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export interface ImageProps extends Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'className' | 'loading' | 'alt'> {
  sizes?: string
  src: string
  srcSet?: number[]
}

interface ImageFallbackProps extends BaseHTMLAttributes<HTMLDivElement> {
  alt?: string
}

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export const LazyImagePlaceholder = styled.img`
  filter: blur(12px);
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 500ms ease-out;
`

const StyledError = styled.div`
  align-items: center;
  color: ${tok('primitives_color_neutral_650')};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 0 ${space('primitives_space_16')};
  text-align: center;
  width: 100%;
`

const StyledText = styled(Typography)`
  margin-top: ${space('primitives_space_8')};
  max-width: 11rem;
`

const StyledImage = styled.img<{ $loaded: boolean }>`
  position: absolute; // to put it on top of the placeholder
  opacity: ${props => (props.$loaded ? 1 : 0)};
  transition: opacity 500ms ease-out;
`

/**
 * @deprecated Try using MediaServerLazyImage instead
 * In case you need to add custom styles, and need to extend Image, make sure you understand how UDS LazyImage works internally
 */
export const ImageWrapper = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
  overflow: hidden; // hide overlapping filter blur from sibilings
  background: ${tok('primitives_color_neutral_150')};
`

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export const LazyImage = styled(LegacyImage)`
  height: 100%;
  object-fit: cover;
  width: 100%;
`

/**
 * @deprecated Use MediaServerLazyImage instead
 */
export const LazyImageFallback = styled(ImageFallback)``

function LegacyImage({ sizes, src, srcSet = [], ...attributes }: ImageProps) {
  const [imageError, setImageError] = useState(false)
  const [imageIsLoaded, setImageIsLoaded] = useState(false)

  useEffect(() => {
    setImageError(false)
    setImageIsLoaded(false)
  }, [src])

  if (imageError) {
    return (
      <ImageWrapper>
        <LazyImageFallback alt={attributes.alt} />
      </ImageWrapper>
    )
  }

  return (
    <ImageWrapper>
      <LazyImagePlaceholder
        {...attributes}
        alt={attributes.alt ?? ''}
        src={buildImgSrc(src, 20)}
        loading="eager"
        onErrorCapture={() => {
          setImageError(true)
        }}
      />
      <StyledImage
        {...attributes}
        $loaded={imageIsLoaded}
        alt={attributes.alt ?? ''}
        onErrorCapture={() => setImageError(true)}
        onLoadCapture={() => setImageIsLoaded(true)}
        sizes={sizes}
        src={srcSet.length > 0 ? buildImgSrc(src, srcSet[0]) : src}
        srcSet={srcSet.length > 0 ? buildImgSrcSet(src, srcSet) : undefined}
      />
    </ImageWrapper>
  )
}

LegacyImage.displayName = 'LazyImage'

function ImageFallback({ alt, ...attributes }: ImageFallbackProps): JSX.Element {
  return (
    <StyledError {...attributes}>
      <Icon glyph="ImageSlashed" size={32} />
      <StyledText variant="body-s-regular-tight">{alt}</StyledText>
    </StyledError>
  )
}
